<script>
import { defineAsyncComponent, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components/widgets/FloatingCallWidget.vue')
);

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import MonitorTopbar from 'dashboard/components-next/topbar/MonitorTopbar.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    FloatingCallWidget,
    MobileSidebarLauncher,
    MonitorTopbar,
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    // Telas que já são um ambiente inteiro escondem o menu do OS.
    //
    // A configuração de um agente tem menu próprio com seis seções; com os dois na tela, o
    // operador via duas barras verticais lado a lado e tinha de descobrir qual era qual —
    // e a de fora não leva a lugar nenhum de dentro do agente. Quem entra num agente saiu do
    // OS; a volta é o "Back to agents" no cabeçalho.
    //
    // Por `meta` da rota e não por nome: outra tela que vire ambiente próprio liga a mesma
    // chave, sem que este componente precise saber quais são.
    escondeMenuDoOS() {
      return Boolean(this.$route.meta?.hideAppSidebar);
    },
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener('message', this.onPowerAppMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onPowerAppMessage);
  },
  methods: {
    // Um PowerApp roda dentro de um iframe, e clique dentro de iframe não chega
    // a este documento — por isso o menu do celular ficava aberto por cima do
    // app depois de aberto, e só o botão o fechava. O app avisa cada toque por
    // postMessage; aqui esse aviso vale como "clicou fora do menu".
    onPowerAppMessage(e) {
      if (e.data?.type === 'powerbizz-app-click') this.closeMobileSidebar();
    },
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      v-if="!escondeMenuDoOS"
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-col flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-background"
    >
      <MonitorTopbar />
      <div class="flex flex-1 w-full min-h-0 overflow-hidden">
        <router-view />
      </div>
      <CommandBar />
      <MobileSidebarLauncher
        :is-mobile-sidebar-open="isMobileSidebarOpen"
        @toggle="toggleMobileSidebar"
      />
      <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
